import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Button,
  Input,
  Flex,
  Image,
  Spinner,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaEye, FaEdit, FaBroom, FaTrash } from 'react-icons/fa';
import car_second_icon from '../Assets/icons/car_second_icon.svg';
import add_new_car from '../Assets/icons/add_new_car.svg';

import { useGetUserVehicles, useDeleteVehicle } from '../utils/vehicles.api';
import { useDeleteConversation } from '../utils/conversation.api';
import { useUIStore } from '../store/uiStore';

const Sidebar = ({ 
  isOpen, 
  isMobile = false,
  onSettingsClick, 
  onVehicleSelect, 
  onClearChat,
  onCloseSidebar,
}) => {
  // Zustand store for vehicle form
  const { openVehicleForm, setVehicleToEdit } = useUIStore();
  const [searchParams] = useSearchParams();
  const urlVehicleId = searchParams.get('vehicleId');
  const [selectedVehicleId, setSelectedVehicleId] = useState(urlVehicleId || null);
  const [, setOpenMenuId] = useState(null);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [vehicleToClearChat, setVehicleToClearChat] = useState(null);
  const [isClearChatDialogOpen, setIsClearChatDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Check if user is logged in (has token)
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userid');

  // Fetch user vehicles only if logged in
  const { data: vehicles, isLoading, error } = useGetUserVehicles(userId);

  // Delete vehicle mutation
  const deleteVehicleMutation = useDeleteVehicle();

  // Delete conversation mutation
  const deleteConversationMutation = useDeleteConversation();

  // Sync selectedVehicleId with URL parameter
  useEffect(() => {
    if (urlVehicleId) {
      setSelectedVehicleId(urlVehicleId);
    }
  }, [urlVehicleId]);

  console.log('vehicles', vehicles);
  console.log(
    'vehicles structure:',
    vehicles
      ? vehicles.map(v => ({
          id: v.id,
          make: v.make,
          model: v.model,
          conversation_id: v.conversation_id,
        }))
      : 'No vehicles'
  );

  const closeSidebarOnMobile = () => {
    if (isMobile && typeof onCloseSidebar === 'function') {
      onCloseSidebar();
    }
  };

  // Handle vehicle selection
  const handleVehicleSelect = vehicle => {
    if (selectedVehicleId === vehicle.id) {
      closeSidebarOnMobile();
      return;
    }

    console.log('Sidebar: Vehicle clicked:', vehicle);
    setSelectedVehicleId(vehicle.id);
    if (onVehicleSelect) {
      console.log('Sidebar: Calling onVehicleSelect with vehicle:', vehicle);
      onVehicleSelect(vehicle);
    }
    closeSidebarOnMobile();
  };

  // Handle vehicle deletion confirmation
  const handleDeleteConfirm = vehicle => {
    setVehicleToDelete(vehicle);
    setIsDeleteDialogOpen(true);
  };

  // Handle vehicle deletion
  const handleVehicleDelete = async () => {
    if (!vehicleToDelete) return;

    try {
      console.log('Deleting vehicle:', vehicleToDelete);
      const response = await deleteVehicleMutation.mutateAsync(
        vehicleToDelete.id
      );
      console.log('Delete response:', response);

      // Close the dialog
      setIsDeleteDialogOpen(false);
      setVehicleToDelete(null);
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      // Handle error - show error message to user
    }
  };

  // Handle cancel delete
  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setVehicleToDelete(null);
  };

  // Handle clear chat confirmation
  const handleClearChatConfirm = vehicle => {
    setVehicleToClearChat(vehicle);
    setIsClearChatDialogOpen(true);
  };

  // Handle clear chat
  const handleClearChat = async () => {
    if (!vehicleToClearChat || !vehicleToClearChat.conversation_id) return;

    try {
      console.log(
        'Clearing chat for conversation:',
        vehicleToClearChat.conversation_id
      );
      const response = await deleteConversationMutation.mutateAsync(
        vehicleToClearChat.conversation_id
      );
      console.log('Clear chat response:', response);

      // Clear the local chat messages state by calling the callback
      if (onClearChat) {
        onClearChat(vehicleToClearChat.conversation_id);
      }

      // Close the dialog
      setIsClearChatDialogOpen(false);
      setVehicleToClearChat(null);
    } catch (error) {
      console.error('Error clearing chat:', error);
      // Handle error - show error message to user
    }
  };

  // Handle cancel clear chat
  const handleClearChatCancel = () => {
    setIsClearChatDialogOpen(false);
    setVehicleToClearChat(null);
  };

  // Handle edit vehicle
  const handleEditVehicle = vehicle => {
    if (isMobile) {
      navigate('/dashboard/add-vehicle', { state: { vehicleToEdit: vehicle } });
      return;
    }
    setVehicleToEdit(vehicle);
    openVehicleForm(vehicle);
  };

  // Handle view car info
  const handleViewCarInfo = vehicle => {
    console.log('Viewing car info for vehicle:', vehicle);
    // Navigate to carinfo page with vehicle ID
    navigate(`/carinfo?vehicleId=${vehicle.id}`);
    closeSidebarOnMobile();
  };

  // Filter vehicles based on search query
  const filteredVehicles = vehicles
    ? vehicles.filter(vehicle => {
        const searchLower = searchQuery.toLowerCase().trim();
        if (!searchLower) return true;
        const make = (vehicle.make || '').toLowerCase();
        const model = (vehicle.model || '').toLowerCase();
        const fullName = `${make} ${model}`.trim();
        return make.includes(searchLower) || model.includes(searchLower) || fullName.includes(searchLower);
      })
    : [];

  const handleAddNewVehicle = () => {
    if (isMobile) {
      navigate('/dashboard/add-vehicle');
      return;
    }
    openVehicleForm();
  };

  if (!isOpen) return null;

  return (
    <>
      <Box
        w={{ base: '100%', md: '300px' }}
        bg="white"
        borderRight="1px"
        borderRightColor="gray.200"
        h="calc(100vh - 80px)"
        overflowY="auto"
        display="flex"
        flexDirection="column"
        // sx={{
        //   '::-webkit-scrollbar': { display: 'none' },
        //   msOverflowStyle: 'none',
        //   scrollbarWidth: 'none',
        // }}
      >
        {/* Search Input */}
        <Box p={4} borderBottom="1px" borderBottomColor="gray.100">
          <Flex
            bg="gray.100"
            borderRadius="md"
            px={3}
            py={2}
            align="center"
            gap={2}
          >
            <SearchIcon color="gray.400" />
            <Input
              placeholder="Search vehicles..."
              bg="transparent"
              border="none"
              _focus={{ boxShadow: 'none' }}
              fontSize="sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Flex>
        </Box>

        {/* Vehicle List */}
        <VStack spacing={0} align="stretch" flex={1}>
          {/* Add New Vehicle Button - Always visible */}
          <Button
            variant="ghost"
            justifyContent="start"
            py={3}
            px={4}
            borderRadius={0}
            _hover={{ bg: 'blue.50' }}
            leftIcon={<Image src={add_new_car} alt="Car" w="4" h="4" />}
            onClick={handleAddNewVehicle}
          >
            Add new Vehicle
          </Button>

          {/* Clear Selection Button - Only show when a vehicle is selected */}
          {/* {selectedVehicleId && (
            <Button
              variant="ghost"
              justifyContent="start"
              py={2}
              px={4}
              borderRadius={0}
              _hover={{ bg: 'red.50' }}
              color="red.500"
              fontSize="sm"
              onClick={() => {
                setSelectedVehicleId(null);
                if (onVehicleSelect) {
                  onVehicleSelect(null);
                }
              }}
            >
              Clear Selection
            </Button>
          )} */}

          {/* Dynamic Vehicle List - Only show when logged in */}
          {token && userId ? (
            <>
              {isLoading ? (
                <Flex justify="center" py={4}>
                  <Spinner size="sm" color="blue.500" />
                </Flex>
              ) : error ? (
                <Text px={4} py={2} color="red.500" fontSize="sm">
                  Failed to load vehicles
                </Text>
              ) : filteredVehicles && filteredVehicles.length > 0 ? (
                filteredVehicles.map(vehicle => (
                  <Button
                    key={vehicle.id}
                    variant="ghost"
                    justifyContent="space-between"
                    py={3}
                    px={4}
                    borderRadius={0}
                    bg={
                      selectedVehicleId === vehicle.id
                        ? 'blue.200'
                        : 'transparent'
                    }
                    color={
                      selectedVehicleId === vehicle.id ? 'blue.700' : 'inherit'
                    }
                    borderLeft={
                      selectedVehicleId === vehicle.id ? '3px solid' : 'none'
                    }
                    borderLeftColor={
                      selectedVehicleId === vehicle.id
                        ? 'blue.500'
                        : 'transparent'
                    }
                    _hover={{
                      bg:
                        selectedVehicleId === vehicle.id
                          ? 'blue.200'
                          : 'blue.50',
                    }}
                    leftIcon={
                      <Image src={car_second_icon} alt="Car" w="4" h="4" />
                    }
                    onClick={() => {
                      console.log('Vehicle button clicked:', vehicle);
                      console.log(
                        'Vehicle has conversation_id:',
                        vehicle.conversation_id
                      );
                      handleVehicleSelect(vehicle);
                    }}
                    height="auto"
                    minH="60px"
                  >
                    <VStack align="start" spacing={0} flex={1}>
                      <Text 
                        fontWeight={selectedVehicleId === vehicle.id ? "bold" : "medium"} 
                        fontSize="sm"
                      >
                        {vehicle.make} {vehicle.model}
                      </Text>
                      {/* <Text fontSize="xs" color="gray.500">
                        {vehicle.year} • {vehicle.engine_type} • {vehicle.mileage.toLocaleString()} mi
                      </Text> */}
                    </VStack>
                    <Menu
                      onOpen={() => setOpenMenuId(vehicle.id)}
                      onClose={() => setOpenMenuId(null)}
                    >
                      <MenuButton
                        as={Box}
                        onClick={e => {
                          e.stopPropagation();
                        }}
                        cursor="pointer"
                        p={1}
                        _hover={{ bg: 'gray.100' }}
                        borderRadius="md"
                      >
                        <Text fontSize="lg" color="gray.500">
                          ⋯
                        </Text>
                      </MenuButton>
                      <MenuList minW="120px" py={2}>
                        <MenuItem
                          onClick={e => {
                            e.stopPropagation();
                            handleViewCarInfo(vehicle);
                            setOpenMenuId(null); // Close the menu
                          }}
                          color="green.500"
                          _hover={{ bg: 'green.50' }}
                          py={2}
                          px={6}
                          fontSize="sm"
                        >
                          <Flex align="center" gap={2}>
                            <FaEye />
                            <Text>View Car Info</Text>
                          </Flex>
                        </MenuItem>
                        <MenuItem
                          onClick={e => {
                            e.stopPropagation();
                            handleEditVehicle(vehicle);
                            setOpenMenuId(null); // Close the menu
                          }}
                          color="blue.500"
                          _hover={{ bg: 'blue.50' }}
                          py={2}
                          px={6}
                          fontSize="sm"
                        >
                          <Flex align="center" gap={2}>
                            <FaEdit />
                            <Text>Edit Car</Text>
                          </Flex>
                        </MenuItem>
                        {vehicle.conversation_id && (
                          <MenuItem
                            onClick={e => {
                              e.stopPropagation();
                              handleClearChatConfirm(vehicle);
                              setOpenMenuId(null); // Close the menu
                            }}
                            color="gray.500"
                            _hover={{ bg: 'gray.100' }}
                            py={2}
                            px={6}
                            fontSize="sm"
                          >
                            <Flex align="center" gap={2}>
                              <FaBroom />
                              <Text>Clear Chat</Text>
                            </Flex>
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteConfirm(vehicle);
                            setOpenMenuId(null); // Close the menu
                          }}
                          color="red.500"
                          _hover={{ bg: 'red.50' }}
                          py={2}
                          px={6}
                          fontSize="sm"
                        >
                          <Flex align="center" gap={2}>
                            <FaTrash />
                            <Text>Delete</Text>
                          </Flex>
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Button>
                ))
              ) : vehicles && vehicles.length > 0 && filteredVehicles.length === 0 ? (
                <Text px={4} py={2} color="gray.500" fontSize="sm">
                  No vehicles match your search
                </Text>
              ) : (
                <Text px={4} py={2} color="gray.500" fontSize="sm">
                  No vehicles found
                </Text>
              )}
            </>
          ) : (
            <Text px={4} py={2} color="gray.500" fontSize="sm">
              Please log in to view your vehicles
            </Text>
          )}
        </VStack>

        {/* Settings Button at Bottom */}
        {/* <Box p={3} borderTop="1px" borderTopColor="gray.100">
          <Button
            variant="ghost"
            justifyContent="start"
            py={3}
            px={4}
            w="full"
            borderRadius="md"
            _hover={{ bg: 'blue.50' }}
            leftIcon={<Image src={setting_icon} alt="Settings" w="4" h="4" />}
            onClick={onSettingsClick}
          >
            Settings
          </Button>
        </Box> */}
      </Box>

      {/* Vehicle Details Form Popup - Now managed by DashboardStateManager */}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleDeleteCancel}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Vehicle
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete {vehicleToDelete?.make}{' '}
              {vehicleToDelete?.model}? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={handleDeleteCancel}>Cancel</Button>
              <Button colorScheme="red" onClick={handleVehicleDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Clear Chat Confirmation Dialog */}
      <AlertDialog
        isOpen={isClearChatDialogOpen}
        onClose={handleClearChatCancel}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Clear Chat
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to clear the chat history for{' '}
              {vehicleToClearChat?.make} {vehicleToClearChat?.model}? This
              action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={handleClearChatCancel}>Cancel</Button>
              <Button colorScheme="gray" onClick={handleClearChat} ml={3}>
                Clear Chat
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default Sidebar;
